import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, RotateCw } from 'lucide-react';
import { AuthShell } from '@/components/Auth';
import { Button } from '@/components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { ROUTE } from '@/config';
import useRequestOTP from '@/hooks/useRequestOtp';
import useVerify, { OTP_LENGTH } from '@/hooks/useVerify';
import { formatPhoneDisplay } from '@/utils';

const Verify = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const phoneNumber: string | undefined = location.state?.phoneNumber;
  const { countdown, requestOTP } = useRequestOTP(phoneNumber ?? '');
  const { code, changeCode, submit, failed, isLoading, isComplete } = useVerify();

  // The number only lives in router state, so a refresh or a bookmarked /verify
  // lands here with nothing to verify against. Send them back instead of
  // rendering a screen that can never succeed.
  useEffect(() => {
    if (!phoneNumber) navigate(ROUTE.LOGIN, { replace: true });
  }, [phoneNumber, navigate]);

  if (!phoneNumber) return null;

  return (
    <AuthShell>
      <button
        type='button'
        onClick={() => navigate(ROUTE.LOGIN)}
        className='mb-6 inline-flex items-center gap-1.5 bg-transparent text-sm font-medium text-sd-muted-foreground transition-colors hover:text-sd-foreground'
      >
        <ArrowLeft className='h-4 w-4 rtl:rotate-180' strokeWidth={2} />
        {String(t('auth_back_to_login'))}
      </button>

      <h1 className='text-[26px] font-extrabold leading-tight tracking-[-0.01em] text-sd-foreground'>
        {String(t('auth_verify_title'))}
      </h1>
      <p className='mt-2 text-[15px] leading-relaxed text-sd-muted-foreground'>
        {String(t('auth_verify_subtitle'))}
      </p>

      <div className='mt-3 flex flex-wrap items-center gap-2'>
        <span
          dir='ltr'
          className='rounded-full bg-sd-muted px-3 py-1.5 text-sm font-bold text-sd-foreground'
        >
          {formatPhoneDisplay(phoneNumber)}
        </span>
        <button
          type='button'
          onClick={() => navigate(ROUTE.LOGIN)}
          className='bg-transparent text-sm font-semibold text-sd-link hover:underline'
        >
          {String(t('auth_change_number'))}
        </button>
      </div>

      {/* Codes fill left-to-right in every locale — this block never flips. */}
      <div dir='ltr' className='mt-7 flex justify-center'>
        <InputOTP
          maxLength={OTP_LENGTH}
          value={code}
          onChange={changeCode}
          disabled={isLoading}
          autoFocus
          aria-label={String(t('auth_verify_title'))}
          // The library's hidden input sits under the boxes; the app's global
          // `.sd-scope input` rule would give it padding and shift it.
          className='!p-0'
        >
          <InputOTPGroup className='gap-3'>
            {Array.from({ length: OTP_LENGTH }, (_, index) => (
              <InputOTPSlot
                key={index}
                index={index}
                className={failed ? 'border-sd-destructive text-sd-destructive' : undefined}
              />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>

      {failed ? (
        <p role='alert' className='mt-3 text-center text-[13px] font-medium text-sd-destructive'>
          {String(t('auth_otp_invalid'))}
        </p>
      ) : null}

      <Button
        type='button'
        onClick={submit}
        disabled={!isComplete || isLoading}
        className='mt-7 h-12 w-full rounded-xl text-[15px] font-bold'
      >
        {isLoading ? <Loader2 className='animate-spin' /> : <Check strokeWidth={2.5} />}
        {String(t('auth_verify_cta'))}
      </Button>

      <div className='mt-5 text-center text-sm'>
        {countdown > 0 ? (
          <span className='text-sd-muted-foreground'>
            {String(t('auth_resend_in', { seconds: countdown }))}
          </span>
        ) : (
          <button
            type='button'
            onClick={requestOTP}
            className='inline-flex items-center gap-1.5 bg-transparent font-semibold text-sd-link hover:underline'
          >
            <RotateCw className='h-4 w-4' strokeWidth={2} />
            {String(t('auth_resend'))}
          </button>
        )}
      </div>
    </AuthShell>
  );
};

export default Verify;
